import { useMemo } from 'react';
import { Client } from '../../types/index';
import { exportToCSV, CSVColumn, csvFormatters } from '../../utils/csvExport';

export type ExportType = 'all' | 'active' | 'inactive';

export interface ClientExportOptions {
    type: ExportType;
    filename?: string;
}

/**
 * Hook para exportar clientes a CSV
 * Proporciona funciones para exportar diferentes tipos de clientes
 */
export const useClientExport = (clients: Client[] | undefined) => {
    
    // Definir las columnas para el CSV
    const csvColumns: CSVColumn[] = useMemo(() => [
        {
            key: 'nombre',
            label: 'Nombre'
        },
        {
            key: 'dni',
            label: 'DNI'
        },
        {
            key: 'email',
            label: 'Email'
        },
        {
            key: 'telefono',
            label: 'Teléfono'
        },
        {
            key: 'fechaAlta',
            label: 'Fecha Alta',
            formatter: csvFormatters.date
        },
        {
            key: 'activo',
            label: 'Estado',
            formatter: (value: boolean) => value ? 'Activo' : 'Inactivo'
        },
        {
            key: 'enEspera',
            label: 'En Espera',
            formatter: csvFormatters.boolean
        },
        {
            key: 'fechaInicioEspera',
            label: 'Inicio Espera',
            formatter: csvFormatters.date
        },
        {
            key: 'fechaFinEspera',
            label: 'Fin Espera',
            formatter: csvFormatters.date
        },
        {
            key: 'motivoEspera',
            label: 'Motivo Espera',
            formatter: csvFormatters.cleanText
        },
        {
            key: 'plazas',
            label: 'Cantidad Plazas',
            formatter: (plazas: any[]) => plazas ? plazas.length.toString() : '0'
        },
        {
            key: 'plazasDetalle',
            label: 'Detalle Plazas',
            formatter: (value: any, client: Client) => formatPlazasDetalle(client.plazas)
        },
        {
            key: 'tipoSuscripcion',
            label: 'Tipo Suscripción',
            formatter: (value: any, client: Client) => formatTipoSuscripcion(client.suscripciones)
        },
        {
            key: 'tipoPago',
            label: 'Tipo Pago',
            formatter: (value: any, client: Client) => formatTipoPago(client.suscripciones)
        },
        {
            key: 'observaciones',
            label: 'Observaciones Cliente',
            formatter: csvFormatters.cleanText
        },
        {
            key: 'observacionesSuscripcion',
            label: 'Observaciones Suscripción',
            formatter: (value: any, client: Client) => formatObservacionesSuscripcion(client.suscripciones)
        },
        {
            key: 'createdBy',
            label: 'Registrado Por'
        }
    ], []);

    // Función para filtrar clientes según el tipo
    const filterClients = useMemo(() => (type: ExportType): Client[] => {
        if (!clients) return [];

        switch (type) {
            case 'active':
                return clients.filter(client => client.activo === true);
            case 'inactive':
                return clients.filter(client => client.activo === false);
            case 'all':
            default:
                return clients;
        }
    }, [clients]);

    // Función para obtener estadísticas por tipo
    const getExportStats = useMemo(() => (type: ExportType) => {
        const filteredClients = filterClients(type);
        return {
            total: filteredClients.length,
            withPlazas: filteredClients.filter(c => c.plazas && c.plazas.length > 0).length,
            enEspera: filteredClients.filter(c => c.enEspera === true).length
        };
    }, [filterClients]);

    // Función principal para exportar
    const exportClients = (options: ClientExportOptions) => {
        const { type, filename } = options;
        const filteredClients = filterClients(type);

        if (filteredClients.length === 0) {
            console.warn(`No hay clientes ${type === 'all' ? 'disponibles' : type === 'active' ? 'activos' : 'inactivos'} para exportar`);
            return;
        }

        // Procesar datos para incluir campos calculados
        const processedData = filteredClients.map(client => ({
            ...client,
            plazasDetalle: client.plazas,
            tipoSuscripcion: client.suscripciones,
            tipoPago: client.suscripciones,
            observacionesSuscripcion: client.suscripciones
        }));

        // Generar nombre de archivo
        const defaultFilename = `clientes_${type}_${new Date().toISOString().split('T')[0]}`;
        const exportFilename = filename || defaultFilename;

        // Exportar usando la utilidad CSV
        exportToCSV(processedData, csvColumns, exportFilename);
    };

    return {
        exportClients,
        getExportStats,
        isReady: !!clients && clients.length > 0
    };
};

// Funciones auxiliares para formatear datos específicos

/**
 * Formatea el detalle de plazas para CSV
 */
const formatPlazasDetalle = (plazas: any[] | undefined): string => {
    if (!plazas || plazas.length === 0) return '';
    
    return plazas.map(plaza => {
        const nivel = typeof plaza.nivel_id === 'object' ? plaza.nivel_id.nombre : 'N/A';
        return `${plaza.dia} ${plaza.horaInicio} (${nivel})`;
    }).join('; ');
};

/**
 * Formatea el tipo de suscripción para CSV
 */
const formatTipoSuscripcion = (suscripciones: any[] | undefined): string => {
    if (!suscripciones || suscripciones.length === 0) return '';
    
    const activa = suscripciones.find(sub => sub.activo === true);
    if (activa) {
        if (activa.nombreSuscripcion === 'pilates-domiciliacion') return 'Domiciliación';
        if (activa.nombreSuscripcion === 'pilates-otros-pagos') return 'Otros Pagos';
        return activa.nombreSuscripcion || 'No especificado';
    }
    
    return 'Sin suscripción activa';
};

/**
 * Formatea el tipo de pago para CSV
 */
const formatTipoPago = (suscripciones: any[] | undefined): string => {
    if (!suscripciones || suscripciones.length === 0) return '';
    
    const activa = suscripciones.find(sub => sub.activo === true);
    return activa?.tipoPago || '';
};

/**
 * Formatea las observaciones de suscripción para CSV
 */
const formatObservacionesSuscripcion = (suscripciones: any[] | undefined): string => {
    if (!suscripciones || suscripciones.length === 0) return '';
    
    const observacionesArray = suscripciones
        .filter(sub => sub.observaciones && sub.observaciones.trim() !== '')
        .map(sub => sub.observaciones.trim());
    
    return observacionesArray.join('; ');
};
